import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Clock,
  Calendar,
  Briefcase,
  FileText,
  VideoIcon,
  UserCheck,
  Users,
  Star,
  Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ScheduleInterviewDialog from '../components/ScheduleInterviewDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';

const statusSteps = [
  { id: 'application', label: 'Application Submitted', icon: FileText },
  { id: 'assessment', label: 'Assessment Completed', icon: CheckCircle },
  { id: 'video', label: 'Video Introduction', icon: VideoIcon },
  { id: 'review', label: 'Under Review', icon: Clock },
  { id: 'decision', label: 'Decision', icon: UserCheck }
];

// This would come from a real API in a production app
const mockApplicationData = {
  id: '1234',
  userId: 'user123',
  jobId: '2',
  jobTitle: 'UX/UI Designer',
  company: 'TechRecruit',
  appliedDate: '2023-06-12',
  status: 'reviewed', // new, reviewed, shortlisted, rejected
  currentStep: 'review', // application, assessment, video, review, decision
  completedSteps: ['application', 'assessment', 'video'],
  nextStep: 'decision',
  feedback: null,
  interviewDate: null,
  // New fields for employer interaction stats
  employerStats: {
    viewCount: 8,
    shortlistCount: 3,
    interviewCount: 1
  },
  // New field for scheduled meetings
  scheduledMeetings: [
    { 
      id: '1',
      dateTime: '2023-07-15T14:00:00',
      platform: 'Google Meet',
      link: 'https://meet.google.com/abc-defg-hij',
      notes: 'Initial interview to discuss your experience and the role'
    },
    { 
      id: '2',
      dateTime: '2023-07-20T10:30:00',
      platform: 'Zoom',
      link: 'https://zoom.us/j/1234567890',
      notes: 'Technical assessment with the engineering team'
    },
    { 
      id: '3',
      dateTime: '2023-07-25T16:00:00',
      platform: 'Microsoft Teams',
      link: 'https://teams.microsoft.com/l/meetup-join/abc123',
      notes: 'Final interview with the hiring manager'
    }
  ]
};

const ApplicationStatus = () => {
  const [application, setApplication] = useState(mockApplicationData);
  const [isLoggedIn, setIsLoggedIn] = useState(true); // In a real app, this would come from an auth context
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isInterviewDialogOpen, setIsInterviewDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // In a real app, this would be an API call to get the application status
    // For demo purposes, we're using the mock data
    
    // Check if user is logged in
    const checkLoginStatus = () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoggedIn(false);
        setShowLoginPrompt(true);
        // We don't auto-redirect, just show a prompt
      }
    };
    
    checkLoginStatus();
  }, []);

  // Format date string to a more readable format
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return format(date, 'EEEE, MMMM d, yyyy - h:mm a');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'shortlisted':
        return 'text-green-600';
      case 'rejected':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'New Application';
      case 'reviewed':
        return 'Application Reviewed';
      case 'shortlisted':
        return 'Shortlisted for Interview';
      case 'rejected':
        return 'Not Selected';
      default:
        return 'Unknown Status';
    }
  };

  // Calculate if a meeting is upcoming (in the future)
  const isUpcomingMeeting = (dateTimeString) => {
    const meetingDate = new Date(dateTimeString);
    const currentDate = new Date();
    return meetingDate > currentDate;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16 bg-apple-gray">
        <div className="container mx-auto px-4 md:px-6">
          {showLoginPrompt && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-center">
              <p className="text-yellow-800">
                Sign in to save your application status and receive updates
              </p>
              <Button 
                variant="outline" 
                className="mt-2" 
                onClick={() => navigate('/signup')}
              >
                Sign Up / Login
              </Button>
            </div>
          )}

          <div className="max-w-4xl mx-auto">
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-apple-text mb-2">
                Your Application Status
              </h1>
              <p className="text-apple-text-secondary">
                Track the progress of your job application
              </p>
            </div>

            {/* Employer Interaction Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Profile Views</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="text-2xl font-bold">{application.employerStats.viewCount}</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Employers who viewed your profile
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Shortlisted</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Star className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="text-2xl font-bold">{application.employerStats.shortlistCount}</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Employers who shortlisted you
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Interviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Video className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="text-2xl font-bold">{application.employerStats.interviewCount}</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Employers who scheduled interviews
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Application Header */}
              <div className="border-b border-gray-100 p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <h2 className="text-xl font-semibold text-apple-text">{application.jobTitle}</h2>
                    <div className="text-apple-text-secondary flex items-center mt-1">
                      <Briefcase size={16} className="mr-1.5" />
                      <span>{application.company}</span>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-1.5 text-apple-text-secondary" />
                      <span className="text-apple-text-secondary text-sm">Applied on {application.appliedDate}</span>
                    </div>
                    <div className={`mt-2 font-medium ${getStatusColor(application.status)}`}>
                      {getStatusText(application.status)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Application Progress */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-apple-text mb-6">Application Progress</h3>
                
                <div className="relative">
                  {/* Progress Line */}
                  <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
                  {/* Status Steps */}
                  <div className="space-y-8">
                    {statusSteps.map((step, index) => {
                      const isCompleted = application.completedSteps.includes(step.id);
                      const isCurrent = application.currentStep === step.id;
                      const isUpcoming = !isCompleted && !isCurrent;
                      
                      return (
                        <div key={step.id} className="relative flex items-start">
                          <div className={`relative z-10 flex items-center justify-center w-14 h-14 rounded-full border-2 ${
                            isCompleted 
                              ? 'bg-green-100 border-green-500 text-green-500' 
                              : isCurrent 
                                ? 'bg-blue-100 border-blue-500 text-blue-500'
                                : 'bg-gray-100 border-gray-300 text-gray-400'
                          }`}>
                            <step.icon size={20} />
                          </div>
                          <div className="ml-4 pt-3">
                            <h4 className={`font-medium ${
                              isCompleted 
                                ? 'text-green-600' 
                                : isCurrent 
                                  ? 'text-blue-600'
                                  : 'text-gray-500'
                            }`}>
                              {step.label}
                            </h4>
                            <p className="text-sm mt-1 text-apple-text-secondary">
                              {isCompleted 
                                ? 'Completed' 
                                : isCurrent 
                                  ? 'In Progress'
                                  : 'Upcoming'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Scheduled Interviews */}
              {application.scheduledMeetings && application.scheduledMeetings.length > 0 && (
                <div className="p-6 border-t border-gray-100">
                  <h3 className="text-lg font-medium text-apple-text mb-4">Scheduled Interviews</h3>
                  
                  {/* Upcoming Meetings Section */}
                  {application.scheduledMeetings.some(meeting => isUpcomingMeeting(meeting.dateTime)) && (
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-green-600 mb-3">Upcoming Interviews</h4>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date & Time</TableHead>
                              <TableHead>Platform</TableHead>
                              <TableHead>Notes</TableHead>
                              <TableHead>Meeting Link</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {application.scheduledMeetings
                              .filter(meeting => isUpcomingMeeting(meeting.dateTime))
                              .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
                              .map((meeting) => (
                                <TableRow key={meeting.id} className="bg-green-50">
                                  <TableCell className="font-medium">{formatDateTime(meeting.dateTime)}</TableCell>
                                  <TableCell>{meeting.platform}</TableCell>
                                  <TableCell>{meeting.notes}</TableCell>
                                  <TableCell>
                                    <Button variant="outline" size="sm" className="bg-white hover:bg-green-50" asChild>
                                      <a href={meeting.link} target="_blank" rel="noopener noreferrer">
                                        Join Meeting
                                      </a>
                                    </Button>
                                  </TableCell>
                                </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  {/* Past Meetings Section */}
                  {application.scheduledMeetings.some(meeting => !isUpcomingMeeting(meeting.dateTime)) && (
                    <div>
                      <h4 className="text-md font-medium text-gray-600 mb-3">Past Interviews</h4>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date & Time</TableHead>
                              <TableHead>Platform</TableHead>
                              <TableHead>Notes</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {application.scheduledMeetings
                              .filter(meeting => !isUpcomingMeeting(meeting.dateTime))
                              .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
                              .map((meeting) => (
                                <TableRow key={meeting.id} className="text-gray-500">
                                  <TableCell>{formatDateTime(meeting.dateTime)}</TableCell>
                                  <TableCell>{meeting.platform}</TableCell>
                                  <TableCell>{meeting.notes}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      Completed
                                    </span>
                                  </TableCell>
                                </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Next Steps */}
              <div className="bg-gray-50 p-6 border-t border-gray-100">
                <h3 className="text-lg font-medium text-apple-text mb-4">What's Next?</h3>
                
                {application.status === 'shortlisted' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <CheckCircle className="text-green-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
                      <div>
                        <h4 className="font-medium text-green-800">Congratulations! You've been shortlisted.</h4>
                        <p className="text-green-700 mt-1">
                          Our hiring team was impressed with your application and would like to schedule an interview.
                        </p>
                        {application.interviewDate ? (
                          <p className="text-green-700 mt-2 font-medium">
                            Your interview is scheduled for: {application.interviewDate}
                          </p>
                        ) : (
                          <Button 
                            className="mt-3" 
                            size="sm"
                            onClick={() => setIsInterviewDialogOpen(true)}
                          >
                            Schedule Interview
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {application.status === 'rejected' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <div className="text-red-500 mt-0.5 mr-3 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="15" y1="9" x2="9" y2="15"></line>
                          <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800">Thank you for your interest</h4>
                        <p className="text-red-700 mt-1">
                          We appreciate your application, but we've decided to move forward with other candidates whose qualifications better match our current needs.
                        </p>
                        <Button className="mt-3" size="sm" variant="outline">
                          View Other Opportunities
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {(application.status === 'new' || application.status === 'reviewed') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <Clock className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
                      <div>
                        <h4 className="font-medium text-blue-800">Your application is being reviewed</h4>
                        <p className="text-blue-700 mt-1">
                          Our hiring team is currently reviewing your application. We'll notify you once there's an update.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-apple-text-secondary">
                  If you have any questions about your application, please contact our recruitment team at <a href="mailto:hello@truehr.co" className="text-apple-blue hover:underline">hello@truehr.co</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />

      {/* Interview Dialog */}
      <ScheduleInterviewDialog
        isOpen={isInterviewDialogOpen}
        onClose={() => setIsInterviewDialogOpen(false)}
        candidate={{ 
          name: "John Doe", // This would come from user profile in a real app
          email: "john.doe@example.com" 
        }}
      />
    </div>
  );
};

export default ApplicationStatus;
